import type { FeedPage, ShortVideo } from '~/composables/shorts';
import type { MaybeRefOrGetter } from 'vue';

import {
	mapPropertyToFeedItem,
	uniqueFeedItemsByCommercialId
} from '~/composables/shorts';
import { extractErrorLogContext, useLogger } from '~/composables/useLogger';
import { useStaleGuard } from '~/utils';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';

/** Grade da seção do corretor: 8 vídeos por página, e "Ver mais" traz mais 8. */
export const AGENT_SHORTS_PER_PAGE = 8;

/**
 * Dono da lista de Shorts de um corretor. Faz uma request independente pela
 * rota compartilhada de `featured-videos`, filtrada por `agentSlug`. Não
 * reaproveita `useFeedSession` para manter o estado desta seção isolado do feed
 * global.
 */
export function useAgentShorts(slug: MaybeRefOrGetter<string>) {
	const logger = useLogger('useAgentShorts');
	const guard = useStaleGuard();
	const getCookieData = useCookieData();

	const items = shallowRef<ShortVideo[]>([]);
	const page = ref(1);
	const hasMore = ref(false);
	const isLoading = ref(false);
	const isLoadingMore = ref(false);
	const isLoaded = ref(false);

	const hasVideos = computed(() => items.value.length > 0);

	async function requestPage(
		targetPage: number
	): Promise<{ items: ShortVideo[]; hasMore: boolean } | null> {
		const currentSlug = toValue(slug);

		if (!currentSlug) return null;

		const [result, error] = await nuxtPropertiesRepository.getShorts(
			{
				agentSlug: currentSlug,
				page: targetPage,
				perPage: AGENT_SHORTS_PER_PAGE
			},
			getCookieData()
		);

		if (error || !result) {
			logger.error('Failed to fetch agent shorts', {
				...(error
					? extractErrorLogContext(error)
					: { error_message: 'No agent shorts result returned' }),
				agent_slug: currentSlug,
				page: targetPage
			});
			return null;
		}

		const pageItems = (result.properties ?? [])
			.map(mapPropertyToFeedItem)
			.filter(
				(item): item is ShortVideo & { kind: 'video' } => item?.kind === 'video'
			);

		const pagination = result.pagination;

		return {
			items: pageItems,
			hasMore: (pagination?.page ?? targetPage) < (pagination?.totalPages ?? 1)
		};
	}

	async function load(): Promise<void> {
		const capturedId = guard.next();
		isLoading.value = true;

		try {
			const result = await requestPage(1);
			if (guard.isStale(capturedId)) return;

			items.value = uniqueFeedItemsByCommercialId(result?.items ?? []);
			page.value = 1;
			hasMore.value = result?.hasMore ?? false;
			isLoaded.value = true;
		} finally {
			if (!guard.isStale(capturedId)) isLoading.value = false;
		}
	}

	/**
	 * O botão "Ver mais" some quando o acervo do corretor termina, mas o método
	 * permanece inócuo nesse estado para não depender só da camada de template.
	 */
	async function loadMore(): Promise<boolean> {
		if (isLoading.value || isLoadingMore.value || !hasMore.value) return false;

		const capturedId = guard.next();
		const previousLength = items.value.length;
		isLoadingMore.value = true;

		try {
			const nextPage = page.value + 1;
			const result = await requestPage(nextPage);
			if (guard.isStale(capturedId) || !result) return false;

			items.value = uniqueFeedItemsByCommercialId([
				...items.value,
				...result.items
			]);
			page.value = nextPage;
			hasMore.value = result.hasMore;

			return items.value.length > previousLength;
		} finally {
			if (!guard.isStale(capturedId)) isLoadingMore.value = false;
		}
	}

	/**
	 * Entrega ao reel a lista já carregada a partir do card clicado. O cursor não
	 * pagina além desse recorte para preservar a navegação limpa em
	 * `/shorts/{id}`, conforme o contrato da seção.
	 */
	function snapshot(fromIndex = 0): FeedPage {
		return {
			items: items.value.slice(fromIndex),
			cursor: {
				filters: {},
				page: 1,
				perPage: AGENT_SHORTS_PER_PAGE,
				hasMore: false
			},
			meta: { totalAvailable: Math.max(items.value.length - fromIndex, 0) }
		};
	}

	return {
		items,
		hasMore,
		hasVideos,
		isLoading,
		isLoadingMore,
		isLoaded,
		load,
		loadMore,
		snapshot
	};
}
